import { computed, readonly, ref } from 'vue';
import { faMessages } from './messages.fa';
import { primeVueFaLocale } from './primeVueLocales';

const appLocale = ref('en');
const originalText = new WeakMap();
const originalAttributes = new WeakMap();
const translatedValues = new Set(Object.values(faMessages));
const translatableAttributes = ['placeholder', 'title', 'aria-label', 'aria-placeholder'];
let observer = null;
let primeVueConfig = null;
let primeVueEnglishLocale = null;

const dynamicTranslations = [
  [/^Search results\s*[·-]\s*(\d+)$/i, (match) => `نتایج جست‌وجو · ${match[1]}`],
  [/^Attempt\s+(\d+)\s+of\s+(\d+)$/i, (match) => `تلاش ${match[1]} از ${match[2]}`],
  [/^Page\s+(\d+)\s+of\s+(\d+)$/i, (match) => `صفحه ${match[1]} از ${match[2]}`],
  [/^(\d+)\s+selected$/i, (match) => `${match[1]} مورد انتخاب شده`],
  [/^(\d+)\s+results?$/i, (match) => `${match[1]} نتیجه`],
  [/^Retry scheduled\s+(.+)$/i, (match) => `تلاش مجدد برنامه‌ریزی‌شده: ${match[1]}`],
  [/^Showing\s+(\d+)\s+to\s+(\d+)\s+of\s+(\d+)$/i, (match) => `نمایش ${match[1]} تا ${match[2]} از ${match[3]}`],
  [
    /^Search\s+(.+)\s+shipments$/i,
    (match) => {
      const workflow = match[1].toLowerCase();
      const workflowLabel = workflow === 'cargo' ? 'کارگو' : workflow === 'courier' ? 'کوریر' : translate(match[1]);
      return `جست‌وجوی محموله‌های ${workflowLabel}`;
    }
  ],
  [/^Stop\s+(\d+)$/i, (match) => `توقف ${match[1]}`],
  [/^Edit\s+(.+)$/i, (match) => `ویرایش ${match[1]}`]
];

const normalizeLocale = (value) => (String(value || '').toLowerCase() === 'fa' ? 'fa' : 'en');

const interpolate = (value, params = {}) =>
  Object.entries(params).reduce(
    (result, [key, replacement]) => result.replaceAll(`{${key}}`, String(replacement)),
    String(value ?? '')
  );

const translateDynamic = (value) => {
  for (const [pattern, formatter] of dynamicTranslations) {
    const match = value.match(pattern);
    if (match) return formatter(match);
  }
  return value;
};

export const translate = (key, params = {}) => {
  const source = String(key ?? '');
  if (appLocale.value !== 'fa' || !source) return interpolate(source, params);
  const translated = faMessages[source] || translateDynamic(source);
  return interpolate(translated, params);
};

const translatePreservingWhitespace = (value) => {
  if (typeof value !== 'string' || !value.trim()) return value;
  const trimmed = value.trim();
  const translated = faMessages[trimmed] || translateDynamic(trimmed);
  if (translated === trimmed) return value;
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  return `${leading}${translated}${trailing}`;
};

const shouldSkipNode = (node) => {
  const parent = node.nodeType === 1 ? node : node.parentElement;
  return Boolean(parent?.closest('script, style, code, pre, [data-no-translate], [translate="no"]'));
};

const translateTextNode = (node) => {
  if (!node?.nodeValue || shouldSkipNode(node)) return;
  const current = node.nodeValue;
  const trimmed = current.trim();
  if (!trimmed || translatedValues.has(trimmed)) return;
  const translated = translatePreservingWhitespace(current);
  if (translated === current) return;
  originalText.set(node, current);
  node.nodeValue = translated;
};

const translateAttributes = (element) => {
  if (!(element instanceof Element) || shouldSkipNode(element)) return;
  const stored = originalAttributes.get(element) || {};
  let changed = false;

  translatableAttributes.forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const current = element.getAttribute(attribute);
    if (!current || translatedValues.has(current.trim())) return;
    const translated = translatePreservingWhitespace(current);
    if (translated === current) return;
    stored[attribute] = current;
    element.setAttribute(attribute, translated);
    changed = true;
  });

  if (changed) originalAttributes.set(element, stored);
};

const visitTextNodes = (root, callback) => {
  const documentRef = root?.ownerDocument || (root?.nodeType === 9 ? root : document);
  const showText = documentRef.defaultView?.NodeFilter?.SHOW_TEXT || 4;
  const walker = documentRef.createTreeWalker(root, showText);
  let node = walker.nextNode();
  while (node) {
    callback(node);
    node = walker.nextNode();
  }
};

const translateTree = (root) => {
  if (!root || appLocale.value !== 'fa') return;
  if (root.nodeType === 3) {
    translateTextNode(root);
    return;
  }
  if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
  if (root.nodeType === 1) translateAttributes(root);
  root.querySelectorAll?.('*').forEach(translateAttributes);
  visitTextNodes(root, translateTextNode);
};

const restoreTree = (root) => {
  if (!root) return;
  if (root.nodeType === 3) {
    const source = originalText.get(root);
    if (source !== undefined) root.nodeValue = source;
    return;
  }
  if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;

  const restoreElement = (element) => {
    const stored = originalAttributes.get(element);
    if (!stored) return;
    Object.entries(stored).forEach(([attribute, value]) => element.setAttribute(attribute, value));
  };

  if (root.nodeType === 1) restoreElement(root);
  root.querySelectorAll?.('*').forEach(restoreElement);
  visitTextNodes(root, (node) => {
    const source = originalText.get(node);
    if (source !== undefined) node.nodeValue = source;
  });
};

const cloneLocale = (value) => JSON.parse(JSON.stringify(value || {}));

const applyPrimeVueLocale = () => {
  if (!primeVueConfig?.locale) return;
  const selected = appLocale.value === 'fa' ? primeVueFaLocale : primeVueEnglishLocale;
  if (!selected) return;
  Object.keys(primeVueConfig.locale).forEach((key) => delete primeVueConfig.locale[key]);
  Object.assign(primeVueConfig.locale, cloneLocale(selected));
};

const applyDocumentLocale = () => {
  if (typeof document === 'undefined') return;
  const isPersian = appLocale.value === 'fa';
  document.documentElement.lang = appLocale.value;
  document.documentElement.dir = isPersian ? 'rtl' : 'ltr';
  document.documentElement.classList.toggle('app-rtl', isPersian);
  document.body?.setAttribute('dir', isPersian ? 'rtl' : 'ltr');
  document.body?.classList.toggle('app-rtl', isPersian);
  if (isPersian) translateTree(document.body);
  else restoreTree(document.body);
};

export const setAppLocale = (value) => {
  const nextLocale = normalizeLocale(value);
  appLocale.value = nextLocale;
  applyPrimeVueLocale();
  applyDocumentLocale();
  return nextLocale;
};

export const registerPrimeVueLocale = (config) => {
  primeVueConfig = config || null;
  if (primeVueConfig?.locale && !primeVueEnglishLocale) {
    primeVueEnglishLocale = cloneLocale(primeVueConfig.locale);
  }
  applyPrimeVueLocale();
};

export const startDomTranslation = () => {
  if (typeof document === 'undefined' || observer) return;
  const target = document.body;
  if (!target) return;

  observer = new MutationObserver((mutations) => {
    if (appLocale.value !== 'fa') return;
    mutations.forEach((mutation) => {
      if (mutation.type === 'characterData') {
        translateTextNode(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(translateTree);
      if (mutation.type === 'attributes') translateAttributes(mutation.target);
    });
  });

  observer.observe(target, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: translatableAttributes
  });
  applyDocumentLocale();
};

export const stopDomTranslation = () => {
  observer?.disconnect();
  observer = null;
};

export function useAppI18n() {
  return {
    locale: readonly(appLocale),
    direction: computed(() => (appLocale.value === 'fa' ? 'rtl' : 'ltr')),
    isRtl: computed(() => appLocale.value === 'fa'),
    t: translate,
    setLocale: setAppLocale
  };
}

export { appLocale };
